import {memo} from 'react';

import styled from 'styled-components';

import {InnerWidthContainer} from '~/shared/components/styled';
import {media} from '~/shared/theme/media';
import {body16Bold} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexColumnCenterHorizontally} from '~/shared/theme/FlexLayout';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {facebookPage, instagramProfile, tiktokProfile} from '~/shared/consts/socialConsts';
import {brandName} from '~/shared/config';
import {useIsAndroid, useIsIOS} from '~/shared/hooks/deviceInfo';
import {Link} from '~/shared/router';
import {ETHICS_HOTLINE_URL, UNIFIED_APP_DOWNLOAD_LINK} from '~/shared/consts/commonConsts';
import FooterDownloadTheApp from '~/shared/components/PageFooter/shared/FooterDownloadTheApp';

import FacebookIcon from '../assets/facebookIcon.svg?react';
import InstagramIcon from '../assets/instagramIcon.svg?react';

import FooterLogoImage from './assets/JustEatLogoSlogan.svg?react';
import TiktokIcon from './assets/tiktokIcon.svg?react';

const Root = styled.footer.attrs({
  'data-test-id': 'page-footer',
})<{isBannersShown: boolean}>`
  padding: 40px 16px;
  background-color: ${({theme}) => theme.colors.backgroundInteractive};
  color: ${({theme}) => theme.colors.primaryText};
  ${({isBannersShown}) => isBannersShown && 'padding-bottom: 80px;'}
  ${media.minLargeMobile`
    padding: 60px 0;  
  `}
  ${media.minDesktop`
    ${({isBannersShown}) => isBannersShown && 'padding-bottom: 68px;'}
  `}
`;

const StyledInnerWidthContainer = styled(InnerWidthContainer)`
  ${flexColumnCenterHorizontally}
`;

const FooterLogo = styled(FooterLogoImage).attrs({
  viewBox: '0 0 198 53',
})`
  width: 149px;
  height: 40px;
  margin-bottom: 40px;
  ${media.minLargeTablet`
    width: 198px;
    height: 53px;
  `}
`;

const Title = styled.div`
  ${body16Bold}
`;

const SocialWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  ${media.minLargeTablet`
    padding-bottom: 40px;
    margin-bottom: 40px;
    gap: 40px;
  `}
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 40px;
  width: 100%;
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  ${media.minLargeMobile`
    flex-direction: row;
    justify-content: space-between;
    gap: inherit;
  `}
  ${media.minLargeTablet`
    padding-bottom: 40px;
    margin-bottom: 40px;
  `}
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  color: ${({theme}) => theme.colors.primaryText};
  flex: 1 33.33%;

  a {
    color: ${({theme}) => theme.colors.primaryText};

    &:hover {
      text-decoration: underline;
    }
  }

  .download-the-app-wrapper {
    gap: 16px;
  }

  ${media.minLargeMobile`
    &:last-child {
      margin-top: 40px;
    }
  `}
  ${media.minLargeTablet`
    gap: 8px;
    flex: 1 25%;
    align-items: flex-start;
    ${flipOnLTR`
      text-align: right;
    `}
    &:last-child {
      flex: auto;
      margin-top: 0px;
      gap: 16px;
      align-items: flex-end;
    }
  `}
`;

const FooterSection = styled(Section)`
  ${media.minLargeMobile`
    &:last-child {
      margin-top: 0;
    }
  `}
  ${media.minLargeTablet`
    flex-direction: row;
    gap: 40px;
    &:last-child {
      gap: 40px;
    }
  `}
`;

const Copyright = styled.div`
  direction: ltr;
`;

const SocialLink = styled.a`
  display: block;
  width: 35px;
  height: 35px;

  &:hover {
    svg path {
      fill: ${({theme}) => theme.colors.actionWhiteHover};
    }
  }
`;

const FOOTER_LINKS_MAP = [{
  titleKey: 'footer_about_us',
  links: [{
    labelKey: 'who_are_we',
    href: '/about-us',
  }, {
    labelKey: 'careers',
    href: 'https://careers.justeattakeaway.com/global/en',
    target: '_blank',
  }, {
    labelKey: 'footer_contact_us',
    href: 'mailto:service@just-eat.co.il',
    target: '_blank',
  }],
}, {
  titleKey: 'terms_of_use',
  links: [{
    labelKey: 'terms_of_use_header',
    href: '/terms-of-use',
  }, {
    labelKey: 'privacy_policy',
    href: '/privacy-policy',
  }, {
    labelKey: 'site_map',
    href: '/sitemap',
  }, {
    labelKey: 'accessibility_statement',
    href: '/accessibility-statement',
  }],
}, {
  titleKey: 'explore',
  links: [{
    labelKey: 'footer_just_eat_chains',
    href: '/chains',
  }],
}];

const Footer = ({isBannersShown}: {isBannersShown: boolean}) => {
  const {t, currentLanguageKey} = getLocalizationService();
  const isIOS = useIsIOS();
  const isAndroid = useIsAndroid();

  return (
    <Root isBannersShown={isBannersShown}>
      <StyledInnerWidthContainer>
        <FooterLogo />
        <Title>{t('follow_us')}</Title>
        <SocialWrapper>
          <SocialLink href={facebookPage} target="_blank" rel="noreferrer" aria-label={t('visit_our_facebook_profile')}><FacebookIcon alt="Facebook" /></SocialLink>
          <SocialLink href={instagramProfile} target="_blank" rel="noreferrer" aria-label={t('visit_our_instagram_profile')}><InstagramIcon alt="Instagram" /></SocialLink>
          <SocialLink href={tiktokProfile} target="_blank" rel="noreferrer" aria-label={t('visit_our_tiktok_profile')}><TiktokIcon alt="TikTok" /></SocialLink>
        </SocialWrapper>

        <Sections>
          {FOOTER_LINKS_MAP.map(section => (
            <Section key={section.titleKey}>
              <Title>{t(section.titleKey)}</Title>
              {section.links.map(link => (
                <Link key={link.labelKey} href={link.href} target={link.target}>{t(link.labelKey)}</Link>
              ))}
            </Section>
          ))}
          <Section>
            <FooterDownloadTheApp
              isAndroid={isAndroid}
              isIOS={isIOS}
              unifiedLink={UNIFIED_APP_DOWNLOAD_LINK}
              noContainer
            />
          </Section>
        </Sections>

        <FooterSection>
          <Link href="/report-a-bug">{t('report_bug')}</Link>
          <Link href="/report-vulnerability">{t('report_vulnerability')}</Link>
          <Link href={ETHICS_HOTLINE_URL[currentLanguageKey]} target="_blank" rel="noreferrer">{t('ethics_hotline')}</Link>
          <Copyright>&copy; {new Date().getFullYear()} {brandName}</Copyright>
        </FooterSection>
      </StyledInnerWidthContainer>
    </Root>
  );
};

export default memo(Footer);
